import React, { useMemo } from 'react';
import useAppStore, { useCalculationsSelector } from '../../store/appStore';
import { computeStaffBonusPreview, type StaffBonusPreviewRow } from '../../store/selectors/staffBonusPreview';

const formatMoney = (n: number, symbol = '€') =>
    `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(n || 0)} ${symbol}`;

interface TeamBonusPreviewPanelProps {
    currencySymbol?: string;
    /** Kompaktere Darstellung für schmale Spalten */
    compact?: boolean;
    className?: string;
    /** Wenn false: Panel im Einzel-Bonus-Modus ausblenden */
    showIndividual?: boolean;
}

const groupRows = (rows: StaffBonusPreviewRow[]) => {
    const map = new Map<string, StaffBonusPreviewRow[]>();
    rows.forEach((r) => {
        const key = r.groupLabel || 'Team';
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(r);
    });
    return Array.from(map.entries()).map(([label, list]) => ({
        label,
        list,
        sumMonatHj1: list.reduce((s, r) => s + r.bonusMonatHj1, 0),
        sumMonatHj2: list.reduce((s, r) => s + (r.bonusMonatHj2 || 0), 0),
    }));
};

/**
 * Bonus-Vorschau (Team- oder Einzelmodus) — nur monatliche Werte je Halbjahr.
 * Schreibt nichts in Personaldaten oder die laufende Kalkulation.
 */
export const TeamBonusPreviewPanel: React.FC<TeamBonusPreviewPanelProps> = ({
    currencySymbol = '€',
    compact = false,
    className = '',
    showIndividual = true,
}) => {
    const clientData = useAppStore((s) => s.clientData);
    const settings = useAppStore((s) => s.settings);
    const calc = useCalculationsSelector();

    const preview = useMemo(
        () =>
            computeStaffBonusPreview(clientData, {
                mwstSatz: calc?.mwstSatz,
                lohnfaktorAktiv: calc?.lohnfaktorAktiv,
                geraeteListe: calc?.geraeteListe,
                wochensatz: settings?.global?.Wochensatz || 4.33,
                lohnfaktorData: (calc as any)?.lohnfaktorData || (clientData as any)?.lohnfaktorData,
                isVatExempt: Boolean((clientData as any)?.isVatExempt || (clientData as any)?.umsatzsteuerbefreit),
            }),
        [clientData, calc, settings]
    );

    if (preview.mode === 'individual' && !showIndividual) return null;

    const grouped = groupRows(preview.rows);
    const isTeam = preview.mode === 'team';
    const colClass = compact
        ? 'grid-cols-[minmax(0,1fr)_6.5rem_6.5rem]'
        : 'grid-cols-[minmax(0,1fr)_7.5rem_7.5rem]';

    return (
        <div
            className={`rounded-xl border border-amber-500/40 bg-amber-500/5 dark:bg-amber-950/20 ${
                compact ? 'p-3' : 'p-5'
            } ${className}`}
        >
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                    <p
                        className={`font-black uppercase tracking-widest text-amber-800 dark:text-amber-400 mb-1 ${
                            compact ? 'text-xs' : 'text-sm'
                        }`}
                    >
                        {isTeam ? 'Team-Bonus · Vorschau' : 'Einzel-Boni · Vorschau'}
                    </p>
                    <p
                        className={`font-bold text-pe-text dark:text-white ${
                            compact ? 'text-base' : 'text-xl'
                        }`}
                    >
                        {isTeam
                            ? `Pool ${formatMoney(preview.poolAmount, currencySymbol)} / Monat`
                            : `${preview.selectedCount} Mitarbeiter mit Leistungsdaten`}
                    </p>
                    <p className={`mt-1 text-pe-muted ${compact ? 'text-xs' : 'text-sm'}`}>
                        Nur monatliche Werte — getrennt nach 1. und 2. Halbjahr
                    </p>
                </div>
                <div className={`text-right text-pe-muted ${compact ? 'text-sm' : 'text-base'}`}>
                    {isTeam ? (
                        <>
                            <div className="font-semibold text-pe-text dark:text-slate-200">
                                {preview.selectedCount} Personen
                            </div>
                            <div className="font-mono font-semibold">
                                Σ Gehalt {formatMoney(preview.sumSelectedGrundgehalt, currencySymbol)}
                            </div>
                        </>
                    ) : (
                        <div className="font-semibold text-pe-text dark:text-slate-200">
                            Monatlich · 1. HJ | 2. HJ
                        </div>
                    )}
                </div>
            </div>

            {grouped.length === 0 ? (
                <p className="text-sm text-pe-muted italic">
                    {isTeam
                        ? 'Noch niemand im PE-TeamBonus ausgewählt — Vorschau 0.'
                        : 'Keine Einzel-Boni berechenbar (Leistungsdaten fehlen).'}
                </p>
            ) : (
                <div className={`space-y-3 ${compact ? 'space-y-2' : ''}`}>
                    <div
                        className={`grid gap-2 px-2.5 font-black uppercase tracking-wider text-pe-muted ${colClass} ${
                            compact ? 'text-[11px]' : 'text-xs'
                        }`}
                    >
                        <span>Name</span>
                        <span className="text-right text-amber-800 dark:text-amber-400">1. HJ / Mon.</span>
                        <span className="text-right text-amber-800 dark:text-amber-400">
                            {preview.isHJ2Freigegeben ? '2. HJ / Mon.' : '2. HJ (gesperrt)'}
                        </span>
                    </div>

                    {grouped.map((g) => (
                        <div key={g.label}>
                            <div className="flex items-center justify-between mb-1.5 px-0.5">
                                <span
                                    className={`font-black uppercase tracking-widest text-amber-800 dark:text-amber-400 ${
                                        compact ? 'text-[11px]' : 'text-xs'
                                    }`}
                                >
                                    {g.label}
                                </span>
                            </div>
                            <div className="space-y-1 rounded-lg border border-pe-border dark:border-slate-800 overflow-hidden">
                                {g.list.map((row) => (
                                    <div
                                        key={row.id}
                                        className={`grid items-center gap-2 px-2.5 py-2 bg-pe-card/60 dark:bg-black/30 ${colClass} ${
                                            compact ? 'text-sm' : 'text-[15px]'
                                        }`}
                                    >
                                        <div className="min-w-0 truncate text-pe-text dark:text-slate-200 font-semibold">
                                            {row.name}
                                            {isTeam && (
                                                <span className="text-pe-muted font-normal text-sm ml-1.5">
                                                    {row.sharePercent.toFixed(0)} %
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-right shrink-0 font-mono font-bold text-amber-900 dark:text-amber-400">
                                            {formatMoney(row.bonusMonatHj1, currencySymbol)}
                                        </div>
                                        <div className="text-right shrink-0 font-mono font-bold text-amber-900 dark:text-amber-400">
                                            {row.bonusMonatHj2 == null ? (
                                                <span className="text-pe-muted font-semibold">—</span>
                                            ) : (
                                                formatMoney(row.bonusMonatHj2, currencySymbol)
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    <div
                        className={`grid gap-2 px-2.5 pt-3 border-t border-pe-border dark:border-slate-800 font-bold ${colClass} ${
                            compact ? 'text-sm' : 'text-base'
                        }`}
                    >
                        <span className="text-pe-muted uppercase tracking-wider text-xs self-center">
                            Summe / Monat
                        </span>
                        <span className="text-right font-mono text-amber-900 dark:text-amber-400">
                            {formatMoney(preview.sumMonatHj1, currencySymbol)}
                        </span>
                        <span className="text-right font-mono text-amber-900 dark:text-amber-400">
                            {preview.sumMonatHj2 == null
                                ? '—'
                                : formatMoney(preview.sumMonatHj2, currencySymbol)}
                        </span>
                    </div>
                </div>
            )}

            <p className={`text-pe-muted mt-4 leading-snug ${compact ? 'text-xs' : 'text-sm'}`}>
                {isTeam
                    ? 'Team-Anteil am Pool (monatlich). Links 1. HJ, rechts 2. HJ.'
                    : 'Quelle: Leistungsseite — gleicher Leistungslohn (Über-Soll × DL-% + VK-Provision). Links 1. HJ / Mon., rechts 2. HJ / Mon. Minus → 0.'}{' '}
                Nur Vorschau — Auszahlung manuell nach dem Halbjahr in den Personaldaten.
            </p>
        </div>
    );
};

export default TeamBonusPreviewPanel;
